/*
 * <qty-stepper> - quantity input with +/- buttons.
 *
 * Reads data-min, data-max, data-step from the host element.
 * Wires the inner [data-action="decrement"] and [data-action="increment"]
 * buttons to mutate the inner <input type="number"> within min/max.
 * Announces value changes to screen readers via [data-qty-stepper-announce].
 * Dispatches a `qty-stepper:change` CustomEvent (debounced 50ms) with
 * { value, name } detail. Bubbles to document.
 *
 * 50ms is short enough that single clicks feel instant but long enough to
 * coalesce a chord of rapid clicks (e.g. ++ pressed within one render frame)
 * into a single mutation. The mutation queue in cart-store then handles any
 * back-to-back changes after that.
 *
 * Without this script the buttons are inert (type="button"), the number
 * input remains editable, and the form still submits the input value.
 */
class QtyStepper extends HTMLElement {
  constructor() {
    super();
    this._onClick = this._onClick.bind(this);
    this._onInput = this._onInput.bind(this);
    this._debounce = null;
  }

  connectedCallback() {
    this.min = parseInt(this.dataset.min || '1', 10);
    this.max = parseInt(this.dataset.max || '99', 10);
    this.step = parseInt(this.dataset.step || '1', 10);
    this.input = this.querySelector('.qty-stepper__input');
    this.dec = this.querySelector('[data-action="decrement"]');
    this.inc = this.querySelector('[data-action="increment"]');
    this.announce = this.querySelector('[data-qty-stepper-announce]');
    if (!this.input) return;

    this.dec.addEventListener('click', this._onClick);
    this.inc.addEventListener('click', this._onClick);
    this.input.addEventListener('input', this._onInput);
    this._syncDisabledState();
  }

  disconnectedCallback() {
    if (this.dec) this.dec.removeEventListener('click', this._onClick);
    if (this.inc) this.inc.removeEventListener('click', this._onClick);
    if (this.input) this.input.removeEventListener('input', this._onInput);
    if (this._debounce) clearTimeout(this._debounce);
  }

  _onClick(event) {
    const action = event.currentTarget.dataset.action;
    const current = parseInt(this.input.value || '0', 10);
    let next = current;
    if (action === 'increment') next = Math.min(this.max, current + this.step);
    if (action === 'decrement') next = Math.max(this.min, current - this.step);
    if (next === current) return;
    this.input.value = String(next);
    this._syncDisabledState();
    this._announce(next);
    this._scheduleChange();
  }

  _onInput() {
    let v = parseInt(this.input.value || '0', 10);
    if (Number.isNaN(v)) v = this.min;
    if (v < this.min) v = this.min;
    if (v > this.max) v = this.max;
    if (String(v) !== this.input.value) this.input.value = String(v);
    this._syncDisabledState();
    this._scheduleChange();
  }

  _syncDisabledState() {
    const v = parseInt(this.input.value || '0', 10);
    if (this.dec) this.dec.disabled = v <= this.min;
    if (this.inc) this.inc.disabled = v >= this.max;
  }

  _announce(value) {
    if (this.announce) this.announce.textContent = String(value);
  }

  _scheduleChange() {
    if (this._debounce) clearTimeout(this._debounce);
    this._debounce = setTimeout(() => {
      this.dispatchEvent(new CustomEvent('qty-stepper:change', {
        bubbles: true,
        detail: { value: parseInt(this.input.value, 10), name: this.input.name }
      }));
    }, 50);
  }
}

if (!customElements.get('qty-stepper')) {
  customElements.define('qty-stepper', QtyStepper);
}
